import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useDeleteSkill } from '@/hooks'
import { getErrorMessage } from '@/api/errors'
import { extractValidationMessage } from '@/lib/validationErrors'
import type { Skill } from '@/types'
import { Trash2, X } from 'lucide-react'
import { toast } from 'sonner'

interface DeleteSkillDialogProps {
  projectId: string
  skill: Skill
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function DeleteSkillDialog({ projectId, skill, open, onOpenChange }: DeleteSkillDialogProps) {
  const deleteSkill = useDeleteSkill(projectId)

  const handleDelete = async () => {
    try {
      await deleteSkill.mutateAsync(skill.id)
      toast.success('Skill deleted successfully')
      onOpenChange(false)
    } catch (error) {
      toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to delete skill')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] rounded-2xl p-8 gap-6 border-none shadow-elevated" hideCloseButton>
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl font-bold text-foreground">Delete Skill?</DialogTitle>
          <DialogClose className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none">
            <X className="h-6 w-6" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <DialogDescription className="text-sm leading-[1.6] text-foreground">
          Are you sure you want to delete the skill "<span className="font-bold">{skill.displayName}</span>"? This action
          cannot be undone — its content, versions, and attachments will be permanently removed.
        </DialogDescription>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="h-10 rounded-lg px-4 font-bold">
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteSkill.isPending}
            className="h-10 rounded-lg px-4 font-bold text-white"
          >
            <Trash2 className="h-5 w-5" />
            {deleteSkill.isPending ? 'Deleting...' : 'Delete Skill'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
